import { ShowWithComedian } from "@punchup/data/legacy";
import TourDateRowThemed from "./TourDateRowThemed";
import { useMemo } from "react";
import { useMultipleWaitlistStatus } from "../hooks/useWaitlistStatus";

const MIN_CARD_WIDTH = 327;

const DEFAULT_EMPTY_HEADING = "No Shows Currently Scheduled";
const DEFAULT_EMPTY_BODY =
  "Check back soon – new shows are added all the time!";

export interface VenueShowsCardGridProps {
  venueSlug: string;
  shows: ShowWithComedian[];
  venueLogoImg: string | null;
  emptyHeading?: string;
  emptyBody?: string;
}

/**
 * Responsive grid of `TourDateRowThemed` show cards with a shared empty state.
 * Used by VenuePageFreeShowsPage, VenuePageShowsTonightPage, and the
 * tag-filtered branch of VenuePageCustomPathPage — they all want identical
 * card layout and only differ in the empty-state copy.
 */
export default function VenueShowsCardGrid({
  venueSlug,
  shows,
  venueLogoImg,
  emptyHeading = DEFAULT_EMPTY_HEADING,
  emptyBody = DEFAULT_EMPTY_BODY,
}: VenueShowsCardGridProps) {
  // Batch-fetch waitlist status so each sold-out row doesn't fire its own
  // /api/v2/user/waitlist request — see WaitlistButton.injectedSubscription.
  const soldOutShowsForWaitlist = useMemo(
    () =>
      shows
        .filter((s) => s.is_sold_out)
        .map((s) => ({ id: s.id, ownerId: s.venue_id, isVenue: true })),
    [shows]
  );
  const { getSubscriptionForShow } = useMultipleWaitlistStatus(
    soldOutShowsForWaitlist
  );

  if (shows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 md:gap-2 py-12 px-6 h-[200px] w-full bg-neutral-100 rounded-lg">
        <h3 className="text-base md:text-lg text-primary-blue font-sans normal-case">
          {emptyHeading}
        </h3>
        <p className="text-sm text-secondary-blue text-center">{emptyBody}</p>
      </div>
    );
  }

  return (
    <div
      className="grid gap-4 md:gap-6"
      style={{
        gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_CARD_WIDTH}px, 1fr))`,
      }}
    >
      {shows.map((show) => (
        <div key={show.id} className="w-full aspect-[4/5]">
          <TourDateRowThemed
            venueSlug={venueSlug}
            show={show}
            comedians={show.show_comedians}
            mobileView
            venueLogoImg={venueLogoImg}
            cardBackgroundColor=""
            waitlistSubscription={getSubscriptionForShow(show.id)}
          />
        </div>
      ))}
    </div>
  );
}
